/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';

const CloseReasonsBreakdown = ({ data, total, onReasonClick }) => {
    const maxCount = Math.max(...data.map((item) => item.count), 1);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                    Close Reasons
                </Typography>
                <Chip
                    label={`${total} Closed`}
                    size="small"
                    sx={{
                        bgcolor: '#dcfce7',
                        color: '#25D366',
                        fontWeight: 600,
                        fontSize: '12px',
                    }}
                />
            </Box>

            {/* Bars */}
            {data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                        No closed conversations in this period
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {data.map((item, index) => {
                        const percentage = total > 0 ? (item.count / total) * 100 : 0;
                        const barWidth = (item.count / maxCount) * 100;

                        return (
                            <Box
                                key={item.reason || index}
                                onClick={() => onReasonClick && onReasonClick(item)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    cursor: onReasonClick ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '8px',
                                    p: 1,
                                    mx: -1,
                                    '&:hover': onReasonClick ? {
                                        bgcolor: '#f0fdf4',
                                        transform: 'translateX(4px)',
                                    } : {},
                                }}
                            >
                                {/* Reason label */}
                                <Typography
                                    sx={{
                                        width: '120px',
                                        minWidth: '120px',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: '#334155',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={item.label}
                                >
                                    {item.label}
                                </Typography>

                                {/* Bar */}
                                <Box sx={{ flex: 1, position: 'relative' }}>
                                    <Box
                                        sx={{
                                            height: '24px',
                                            bgcolor: '#dcfce7',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${barWidth}%`,
                                                height: '100%',
                                                background: 'linear-gradient(90deg, #25D366, #128C7E)',
                                                borderRadius: '4px',
                                                transition: 'width 0.5s ease',
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Count and percentage */}
                                <Box sx={{ textAlign: 'right', minWidth: '60px' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            color: '#25D366',
                                        }}
                                    >
                                        {item.count}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: '#94a3b8',
                                        }}
                                    >
                                        {percentage.toFixed(0)}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default CloseReasonsBreakdown;

CloseReasonsBreakdown.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            reason: PropTypes.string,
            label: PropTypes.string,
            count: PropTypes.number,
            threadIds: PropTypes.array,
        })
    ),
    total: PropTypes.number,
    onReasonClick: PropTypes.func,
};

CloseReasonsBreakdown.defaultProps = {
    data: [],
    total: 0,
    onReasonClick: null,
};
